import React, { useState, useEffect } from 'react';

// Global state for lightning settings
window.__lightningSettings = window.__lightningSettings || { snakeWidth: 19.5 };

// ── Shader config defaults ────────────────────────────────────────────────────
window.__snakeShaderConfig = window.__snakeShaderConfig || {
  taperStart: 0.40,
  taperMin: 0.46,
  capR: 0.020,
  coreEdge: 0.50,
  haloEdge: 0.85,
  outerEdge: 1.00,
  maskEdge: 1.05,
};

const LightningDebugPanel = () => {
  const [snakeWidth, setSnakeWidth] = useState(window.__lightningSettings.snakeWidth);
  const [isVisible, setIsVisible] = useState(false); // Ẩn mặc định

  // ── Manual control state ───────────────────────────────────────────────────
  const [manualMode, setManualMode] = useState(false);
  const [manualProgress, setManualProgress] = useState(0);
  const [hasInstance, setHasInstance] = useState(false);

  // ── Shader param state ────────────────────────────────────────────────────
  const cfg = window.__snakeShaderConfig;
  const [taperStart, setTaperStart] = useState(cfg.taperStart);
  const [taperMin, setTaperMin] = useState(cfg.taperMin);
  const [capR, setCapR] = useState(cfg.capR);
  const [coreEdge, setCoreEdge] = useState(cfg.coreEdge);
  const [haloEdge, setHaloEdge] = useState(cfg.haloEdge);
  const [outerEdge, setOuterEdge] = useState(cfg.outerEdge);
  const [maskEdge, setMaskEdge] = useState(cfg.maskEdge);

  const applyTaper = () => {
    const inst = window.__lightningInstance;
    if (!inst || !inst.snakeUniforms) return;
    inst.snakeUniforms.uTaperStart.value = taperStart;
    inst.snakeUniforms.uTaperMin.value = taperMin;
    // Rebuild shader source with new taper params (capR/halo stay as-is)
    const newFrag = inst._buildSnakeFrag({
      taperStart, taperMin,
      capR, coreEdge, haloEdge, outerEdge, maskEdge,
    });
    inst.snakeMesh.material.fragmentShader = newFrag;
    inst.snakeMesh.material.needsUpdate = true;
  };

  // ── Manual mode handlers ──────────────────────────────────────────────────
  const handleManualToggle = (checked) => {
    const inst = window.__lightningInstance;
    setManualMode(checked);
    if (inst) {
      inst.setManualMode(checked, manualProgress);
      setHasInstance(true);
    }
  };

  const handleManualProgress = (val) => {
    const v = parseFloat(val);
    setManualProgress(v);
    const inst = window.__lightningInstance;
    if (inst && manualMode) {
      inst.setManualProgress(v);
    }
  };

  // ── Reload shader với params mới (thay đổi capR, core, halo, outerG) ────
  const reloadShader = () => {
    const inst = window.__lightningInstance;
    if (!inst) return;

    // Cập nhật lại shader source với params mới
    const newFrag = inst._buildSnakeFrag({
      capR: capR,
      coreEdge: coreEdge,
      haloEdge: haloEdge,
      outerEdge: outerEdge,
      maskEdge: maskEdge,
    });

    inst.snakeMesh.material.fragmentShader = newFrag;
    inst.snakeMesh.material.needsUpdate = true;
  };

  // ── Check instance presence mỗi 500ms ────────────────────────────────────
  useEffect(() => {
    const tick = setInterval(() => {
      setHasInstance(!!window.__lightningInstance);
    }, 500);
    return () => clearInterval(tick);
  }, []);

  // ── Width change → fire event ─────────────────────────────────────────────
  useEffect(() => {
    window.__lightningSettings.snakeWidth = snakeWidth;
    window.dispatchEvent(new CustomEvent('lightning-settings-change'));
  }, [snakeWidth]);

  // ── Taper change → apply immediately ──────────────────────────────────────
  useEffect(() => { applyTaper(); },
    // eslint-disable-next-line
    [taperStart, taperMin,]);

  const btnStyle = {
    width: '100%', padding: '7px 0', marginTop: '6px',
    background: 'rgba(255,221,0,0.12)', border: '1px solid rgba(255,221,0,0.4)',
    color: '#ffdd00', borderRadius: '5px', cursor: 'pointer',
    font: '12px monospace',
  };
  const sliderStyle = { width: '100%', accentColor: '#ffdd00' };

  const valStyle = (active) => ({ color: active ? '#ffdd00' : '#555', fontSize: '12px', textAlign: 'right' });

  return (
    <>
      {/* Toggle button when panel is hidden */}
      {/* {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          style={{
            position: 'fixed', top: '100px', right: '20px', zIndex: 99999,
            padding: '8px 16px',
            background: 'rgba(15,15,20,0.92)',
            color: '#ffdd00', border: '1px solid rgba(255,221,0,0.5)',
            borderRadius: '8px', cursor: 'pointer', font: '12px monospace',
            boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
          }}
        >
          ⚡ Debug
        </button>
      )} */}

      {/* Full panel */}
      {isVisible && (
        <div
          style={{
            position: 'fixed', top: '16px', right: '16px', zIndex: 99999,
            background: 'rgba(10,10,18,0.96)',
            color: '#e8e8e8',
            border: '1px solid rgba(255,221,0,0.35)',
            borderRadius: '10px',
            padding: '14px 16px',
            font: '13px/1.6 monospace',
            minWidth: '310px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
            userSelect: 'none',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ color: '#ffdd00', fontWeight: 'bold', fontSize: '13px' }}>⚡ Lightning Debug</span>
            <button
              onClick={() => setIsVisible(false)}
              style={{ background: 'transparent', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
            >
              ✕
            </button>
          </div>

          {/* Instance status */}
          <div style={{
            fontSize: '11px', color: hasInstance ? '#4f8' : '#f66',
            marginBottom: '8px', padding: '4px 8px',
            background: hasInstance ? 'rgba(0,255,100,0.06)' : 'rgba(255,0,0,0.06)',
            borderRadius: '4px',
          }}>
            {hasInstance ? '● Live instance connected' : '○ No instance detected'}
          </div>

          {/* ── 🎮 Manual Control ──────────────────────────────────────────── */}
          <div style={{
            border: '1px solid rgba(0,220,255,0.3)', borderRadius: '6px',
            padding: '10px', marginBottom: '10px',
            background: 'rgba(0,220,255,0.04)',
          }}>
            <div style={{ fontSize: '12px', color: '#00ddff', fontWeight: 'bold', marginBottom: '8px' }}>
              🎮 Manual Control
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ color: '#aaa', fontSize: '12px' }}>Enable:</span>
              <input
                type="checkbox"
                checked={manualMode}
                onChange={(e) => handleManualToggle(e.target.checked)}
                style={{ accentColor: '#00ddff', cursor: 'pointer', width: '16px', height: '16px' }}
              />
              {manualMode && (
                <span style={{ color: '#00ddff', fontSize: '11px' }}>ACTIVE — auto loop paused</span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '65px', flexShrink: 0, color: '#555', fontSize: '12px' }}>Progress:</span>
              <input
                type="range"
                min="0" max="1" step="0.001"
                value={manualProgress}
                onChange={(e) => handleManualProgress(e.target.value)}
                disabled={!manualMode}
                style={{ ...sliderStyle, flex: 1, accentColor: '#00ddff' }}
              />
              <span style={{ ...valStyle(manualMode), width: '46px' }}>
                {manualProgress.toFixed(3)}
              </span>
            </div>
          </div>

          {/* ── 🔺 Taper ──────────────────────────────────────────────────── */}
          <SliderRow
            label="Taper Start"
            min={0} max={1} step={0.01}
            value={taperStart} onChange={setTaperStart}
            valStyle={valStyle(true)}
          />
          <SliderRow
            label="Taper Min"
            min={0} max={1} step={0.01}
            value={taperMin} onChange={setTaperMin}
            valStyle={valStyle(true)}
          />

          {/* ── 🟡 Width ───────────────────────────────────────────────────── */}
          <div style={{ marginTop: '10px', borderTop: '1px solid rgba(255,221,0,0.15)', paddingTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ color: '#aaa', fontSize: '12px' }}>Snake Width:</span>
              <span style={{ color: '#ffdd00', fontSize: '12px' }}>{snakeWidth.toFixed(1)}</span>
            </div>
            <input
              type="range" min={1} max={60} step={0.5}
              value={snakeWidth}
              onChange={(e) => setSnakeWidth(parseFloat(e.target.value))}
              style={sliderStyle}
            />
          </div>

          {/* ── 💡 Shader Params (tham khảo — cần reload shader) ─────────── */}
          <div style={{
            marginTop: '10px', borderTop: '1px solid rgba(255,221,0,0.15)',
            paddingTop: '8px',
          }}>
            <div style={{ color: '#ffdd00', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
              💡 Shader Params
            </div>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>
              (GLSL hardcoded — reload shader để áp dụng)
            </div>

            <SliderRow label="capR" min={0.001} max={0.2} step={0.001} value={capR} onChange={setCapR} valStyle={valStyle(false)} />
            <SliderRow label="coreEdge" min={0.1} max={1.0} step={0.01} value={coreEdge} onChange={setCoreEdge} valStyle={valStyle(false)} />
            <SliderRow label="haloEdge" min={0.1} max={1.5} step={0.01} value={haloEdge} onChange={setHaloEdge} valStyle={valStyle(false)} />
            <SliderRow label="outerEdge" min={0.5} max={2.0} step={0.01} value={outerEdge} onChange={setOuterEdge} valStyle={valStyle(false)} />
            <SliderRow label="maskEdge" min={0.8} max={1.5} step={0.01} value={maskEdge} onChange={setMaskEdge} valStyle={valStyle(false)} />

            <button style={btnStyle} onClick={reloadShader}>
              🔁 Reload Shader
            </button>
          </div>

          {/* Copy / Log Config button */}
          <button
            style={{ ...btnStyle, marginTop: '8px' }}
            onClick={() => {
              const inst = window.__lightningInstance;
              const config = {
                snakeWidth: snakeWidth,
                taperStart: taperStart,
                taperMin: taperMin,
                capR: capR,
                coreEdge: coreEdge,
                haloEdge: haloEdge,
                outerEdge: outerEdge,
                maskEdge: maskEdge,
                // live uniforms
                uTaperStart: inst?.snakeUniforms?.uTaperStart?.value ?? taperStart,
                uTaperMin: inst?.snakeUniforms?.uTaperMin?.value ?? taperMin,
                bodyLen: inst?.options?.bodyLen ?? 0.30,
                snakeSpeed: inst?.options?.snakeSpeed ?? 2,
              };
              console.log('%c⚡ Lightning Config', 'color:#ffdd00;font-weight:bold', config);
              console.log('Copy snippet:',
                `{\n` +
                `  snakeWidth: ${snakeWidth},\n` +
                `  taperStart: ${taperStart},\n` +
                `  taperMin: ${taperMin},\n` +
                `  capR: ${capR},\n` +
                `  coreEdge: ${coreEdge},\n` +
                `  haloEdge: ${haloEdge},\n` +
                `  outerEdge: ${outerEdge},\n` +
                `  maskEdge: ${maskEdge},\n` +
                `}`
              );
            }}
          >
            📋 Copy Config
          </button>

          {/* Reset button */}
          <button
            style={{ ...btnStyle, marginTop: '8px' }}
            onClick={() => {
              setSnakeWidth(19.5);
              setTaperStart(0.40);
              setTaperMin(0.46);
              setCapR(0.020);
              setCoreEdge(0.50);
              setHaloEdge(0.85);
              setOuterEdge(1.00);
              setMaskEdge(1.05);
              if (window.__lightningInstance) {
                const u = window.__lightningInstance.snakeUniforms;
                u.uTaperStart.value = 0.40;
                u.uTaperMin.value = 0.46;
                // Reload shader with reset params
                const newFrag = window.__lightningInstance._buildSnakeFrag({
                  capR: 0.020, coreEdge: 0.50, haloEdge: 0.85, outerEdge: 1.00, maskEdge: 1.05,
                });
                window.__lightningInstance.snakeMesh.material.fragmentShader = newFrag;
                window.__lightningInstance.snakeMesh.material.needsUpdate = true;
              }
            }}
          >
            ↺ Reset All
          </button>
        </div>
      )}
    </>
  );
};

// ── Tiny reusable slider row ───────────────────────────────────────────────────
const SliderRow = ({ label, min, max, step, value, onChange, valStyle }) => (
  <div style={{ marginBottom: '6px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
      <span style={{ width: '72px', flexShrink: 0, color: '#aaa', fontSize: '12px' }}>{label}:</span>
      <input
        type="range" min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, accentColor: '#ffdd00' }}
      />
      <span style={{ ...valStyle, width: '46px' }}>
        {value.toFixed(step < 0.01 ? 3 : 2)}
      </span>
    </div>
  </div>
);

export default LightningDebugPanel;

